import { useState, useEffect } from 'react';
import { supabase, signIn, signUp, onAuthChange } from '../utils/supabase';

export default function AuthGate({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mode, setMode] = useState('login'); // login or signup
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    // Check current session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = onAuthChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      if (mode === 'login') {
        await signIn(email, password);
      } else {
        await signUp(email, password, fullName);
      }
    } catch (err) {
      setError(err.message);
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-brand-gold animate-pulse font-display text-2xl">What's NEXT</div>
      </div>
    );
  }

  if (user) {
    return children;
  }

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-10">
          <img
            src="https://static.wixstatic.com/media/cf458d_f7329a8f179a4571a1951c4ed9431604~mv2.png"
            alt="Next Experience Group"
            className="h-12 w-auto mx-auto mb-6"
          />
          <h1 className="font-display text-4xl text-white mb-2">
            What's <span className="text-brand-gold">NEXT</span>
          </h1>
          <div className="w-12 h-[1px] bg-brand-gold mx-auto mb-3"></div>
          <p className="text-neutral-500 text-xs tracking-[0.25em] uppercase">Hospitality Business Health Intelligence</p>
        </div>

        {/* Form */}
        <div className="card p-8">
          <h2 className="font-display text-lg text-white mb-6 text-center">Sign In</h2>

          {error && (
            <div className="text-red-400 text-sm bg-red-400/10 border border-red-400/20 rounded-lg px-4 py-2 mb-4">
              {error}
            </div>
          )}

          {/* Microsoft Sign In — only option */}
          <button
            onClick={async () => {
              setError('');
              try {
                const { error } = await supabase.auth.signInWithOAuth({
                  provider: 'azure',
                  options: {
                    scopes: 'email profile openid',
                    redirectTo: window.location.origin,
                  },
                });
                if (error) throw error;
              } catch (err) {
                setError(err.message);
              }
            }}
            className="w-full flex items-center justify-center gap-3 border border-neutral-700 rounded-lg px-6 py-3 hover:bg-neutral-800/50 hover:border-neutral-600 transition-all"
          >
            <svg className="w-5 h-5" viewBox="0 0 21 21" fill="none">
              <rect x="1" y="1" width="9" height="9" fill="#F25022"/>
              <rect x="11" y="1" width="9" height="9" fill="#7FBA00"/>
              <rect x="1" y="11" width="9" height="9" fill="#00A4EF"/>
              <rect x="11" y="11" width="9" height="9" fill="#FFB900"/>
            </svg>
            <span className="text-sm font-medium text-white">Sign in with Microsoft</span>
          </button>

          <p className="text-[10px] text-neutral-600 text-center mt-4">Sign in with your @nextexperience.group Microsoft account</p>
        </div>
      </div>
    </div>
  );
}
